import { observer } from 'mobx-react-lite'
import { useCallback, useEffect } from 'react'
import { useCountries } from 'Services/Cores/Countries/useCountries'
import { Initialize } from 'Services/Cores/Initialize'
import StoreCountry from './GlobalStores/Cores/County/StoreCountry'
import AppRouter from './Routers/AppRouter'
import mobiscroll from '@mobiscroll/react'
import BypassRedirectPath from './Data/BypassRedirectPath.json'
import StoreLoader from 'GlobalStores/Cores/Elements/StoreLoader'
import LoadingBar from 'Components/Elements/LoadingBar/LoadingBar'
import { isMaintenanceMode } from 'Helpers/GlobalHelpers'
import Maintenance from 'Components/Pages/Maintenance'
import TrackingEvents from 'Services/Tracker/TrackingEvents'
import ReactFbq from 'react-fbq'
import { get, lowerCase } from 'lodash'
import { Paths } from 'Routers/Paths'
import storeAuth from 'GlobalStores/User/StoreAuth'
import { useState } from 'react'
import StoreTranslate from 'GlobalStores/Cores/Translator/StoreTranslate'
import storeReCaptcha from 'GlobalStores/StoreReCaptcha'
import { autorun } from 'mobx'
import { useMemo } from 'react'
import { PathHelper } from 'GlobalStores/Cores/Helpers/PathHelper'
import feelGreateAPI from 'GlobalStores/Cores/County/APIs'
import SessionAuth from 'GlobalStores/User/SessionAuth'
import storeUser from 'GlobalStores/User/StoreUser'
import InAppSpy from 'inapp-spy'
import { FeelGreatAPI } from 'Services/FeelGreat/APIs'
import mixpanelCookieService from 'Services/Cookie/MixpanelCookieService'
import CookieConsentModal from 'Components/Elements/CookieConsent/CookieConsentModal'
import AccountConflictModal from 'Components/Elements/AccountConflict/AccountConflictModal'
import mixpanelService from 'Services/Mixpanel/MixpanelService'
import { getNewFeelGreatRedirectUrl } from 'Helpers/NewFeelGreatRedirect'

mobiscroll.settings = {
    theme: 'ios',
    themeVariant: 'light'
}

const App = observer(() => {
    const [{ isInApp }] = useState(() => InAppSpy())
    const country = useCountries()
    // const qs = useMemo(() => new URLSearchParams(window.location.search), [])
    const qs = new URL(window.location.href)

    const [autoLoginLoader, setAutoLoginLoader] = useState(false)

    const [goSelectCountry, setGoSelectCountry] = useState(false)

    const GetSiteVersion = useCallback(() => {
        if (country) {
            feelGreateAPI.getSiteVersion({ country: country.country.toLowerCase() }).then(res => {
                let currentVersion = localStorage.getItem('siteVersion')
                if (!currentVersion) {
                    localStorage.setItem('siteVersion', res.data.version)
                } else if (currentVersion !== res.data.version) {
                    SessionAuth.ResetSessionStorage(false)
                    window.location.reload()
                }
            })
        }
    }, [country])

    useEffect(() => {
        if (isInApp) {
            openInBrowser()
        }
    }, [isInApp])

    const RedirectToLogin = () => {
        const country = StoreCountry.CountryLowerCase()
        localStorage.removeItem('autoLogin')
        window.location.replace(`/${country}/login`)
    }

    const GetAutoLogin = async token => {
        try {
            setAutoLoginLoader(true)
            storeUser.reset()
            storeAuth.reset()
            SessionAuth.RemoveSession(true)

            await checkToken()

            setTimeout(async () => {
                const isLoggedIn = await storeAuth.AutoLogin(token)
                localStorage.removeItem('autoLogin')
                if (isLoggedIn) {
                    // Reveal the app only once we're authenticated, so the homepage never flashes before redirecting
                    setAutoLoginLoader(false)
                } else {
                    RedirectToLogin()
                }
            }, 1000)
        } catch (e) {
            localStorage.removeItem('autoLogin')
            setAutoLoginLoader(false)
            RedirectToLogin()
        }
    }

    function openInBrowser() {
        const url = window.location.href

        // Detect if the user is on Android or iOS
        const isAndroid = /Android/i.test(navigator.userAgent)
        const isIOS = /iPhone|iPad|iPod/i.test(navigator.userAgent)

        // Get the current URL (to avoid infinite redirects)
        const currentUrl = window.location.href

        // Check if we are already on localhost or if it is the intent URL
        const isLocalhost = currentUrl.startsWith('http://localhost')
        const isIntentUrl = currentUrl.startsWith('intent://')

        if (!isLocalhost) {
            if (isAndroid && !isIntentUrl) {
                // Use the intent URL scheme for Android
                const intentUrl = 'intent://' + url.replace(/^https?:\/\//, '') + '#Intent;scheme=https;end'
                window.location.href = intentUrl
            } else if (isIOS) {
                // Open the URL in Safari on iOS
                window.open(url, '_system')
            } else {
                // Fallback for other platforms
                window.open(url, '_blank')
            }
        }
    }

    const checkFirstPathname = () => {
        if (StoreCountry.CountryList().length > 0) {
            const countryList = StoreCountry.CountryList()

            const index = countryList.findIndex(country => {
                const countryName = country.country.toLowerCase() || (country.country2 ? country.country2.toLowerCase() : '')
                return PathHelper.Segment(1) === countryName || BypassRedirectPath.includes(PathHelper.Segment(1))
            })

            return index > -1 ? true : false
        }
    }

    const checkToken = async () => {
        console.log('checkToken and Restore')
        if (SessionAuth.GetLoginExtend('token')) {
            try {
                const response = await storeAuth.RestoreSession(SessionAuth.GetLoginExtends()[StoreCountry.CountryLowerCase()])

                if (response) {
                    StoreLoader.SetApiLoaded('refreshToken', true)
                    try {
                        const responseRefreshToken = await SessionAuth.CheckToken(SessionAuth.GetLoginExtend('token'), SessionAuth.GetLoginExtend('country'), SessionAuth.GetLoginExtend('id'))

                        if (responseRefreshToken.data.token) {
                            sessionStorage.removeItem('refreshTokenRetried')
                            SessionAuth.SetLoginExtend('token', responseRefreshToken.data.token)
                        }
                    } catch (refreshError) {
                        console.log('refreshToken error', refreshError)
                        if (!sessionStorage.getItem('refreshTokenRetried')) {
                            sessionStorage.setItem('refreshTokenRetried', '1')
                            await SessionAuth.RemoveSession(true)

                        }

                        SessionAuth.ResetSessionStorage(false)
                        const base = window.location.pathname.replace(/\/reset$/, '') || '/'
                        window.location.replace(base)
                        return
                    }
                } else {
                    throw new Error('No response')
                }
            } catch (e) {
                console.log('checkToken error, forcing logout', e)
                mixpanelCookieService.removeDeviceId()
                await SessionAuth.RemoveSession(true)
                SessionAuth.ResetSessionStorage()

                setTimeout(() => {
                    const base = window.location.pathname.replace(/\/reset$/, '') || '/'
                    window.location.replace(base)
                }, 1000)
                return
            } finally {
                mixpanelService.initialize()
            }
        } else {
            console.log('No token')
            mixpanelService.initialize()
            StoreLoader.SetApiLoaded('refreshToken', true)
        }
    }

    useEffect(() => {
        const initializeApp = async () => {
            if (country) {
                const redirectUrl = getNewFeelGreatRedirectUrl(window.location, country)
                if (redirectUrl) {
                    window.location.replace(redirectUrl)
                    return
                }

                GetSiteVersion()
                const requestedLanguage = new URLSearchParams(window.location.search).get('lang')
                const currentLanguage = requestedLanguage && hasLanguage(country, requestedLanguage) ? requestedLanguage : country.config.languageSettings.default.replace('/ /g', '')

                let countryLowerCase = country.country.replace(/ /g, '').toLowerCase()

                if (PathHelper.Segment(1) === 'saudi' || PathHelper.Segment(6) === 'saudi' || PathHelper.Segment(4) === 'saudi') {
                    countryLowerCase = country.country2.replace(/ /g, '').toLowerCase()
                }

                const countryShortLowerCase = country.short.replace(/ /g, '').toLowerCase()

                mixpanelCookieService.setCountry(String(country.shorter).toUpperCase())

                StoreTranslate.SetCurrentLanguage(currentLanguage)
                storeReCaptcha.setCurrentLanguage(countryLowerCase, currentLanguage)
                mixpanelCookieService.setLanguage(currentLanguage)

                // set current country to local storage
                localStorage.setItem('currentCountry', countryLowerCase)

                if (localStorage.getItem('autoLogin')) {
                    await GetAutoLogin(localStorage.getItem('autoLogin'))
                } else {
                    await checkToken()
                }

                TrackingEvents.Commit(lowerCase(country.country))

                try {
                    ReactFbq.initialize({
                        id: StoreCountry.FBPixelCode.all
                    })
                    ReactFbq.pageView()
                } catch (e) {
                    console.error('FACEBOOK PIXEL ERROR', e)
                }

                if (PathHelper.Segment(1).toLowerCase() === countryShortLowerCase) {
                    mixpanelCookieService.setIsInitialWithManyRedirects(true)
                    PathHelper.overriderCountryUrl({
                        countryName: countryLowerCase
                    })
                } else if (PathHelper.Segments().length > 2 && isNaN(PathHelper.Segment(2)) && !isBypass()) {
                    // * Case There are refCode (not number) or (Feature or Epic path)

                    // Thinh told integrate with THA only [18JUN2024]
                    if (PathHelper.Segment(1) === 'thailand' && PathHelper.Segment(2) === 'c') {
                        const newUrl = window.location.pathname.replace('/c', '') + window.location.search
                        mixpanelCookieService.setIsInitialWithManyRedirects(true)
                        window.location.replace(newUrl)
                    }

                    if (Paths.includes(PathHelper.Segment(2))) {
                        const newUrl = PathHelper.BuildBasename(countryLowerCase) + window.location.search
                        mixpanelCookieService.setIsInitialWithManyRedirects(true)
                        window.location.replace(newUrl)
                    }
                } else if (window.location.pathname === '/' || PathHelper.Segment(1) !== countryLowerCase) {
                    const newUrl = PathHelper.BuildBasename(countryLowerCase) + window.location.search
                    mixpanelCookieService.setIsInitialWithManyRedirects(true)
                    window.location.replace(newUrl)
                }
            } else {
                StoreTranslate.SetCurrentLanguage('en')
            }
        }
        Initialize()
        initializeApp()
    }, [country, GetSiteVersion])

    /*
     * for redirecting reset password page
     */
    useEffect(() => {
        autorun(diposer => {
            if (StoreCountry.countryList) {
                if (qs.searchParams.get('market') && /new-password/.test(window.location.pathname) && !!country === false) {
                    const marketCountry = StoreCountry.CountryList().find(item => item?.shorter?.toLowerCase() === lowerCase(qs.searchParams.get('market')))
                    const isNewPasswordPath = PathHelper.Segment(1) === 'new-password'
                    const pathnames = window.location.pathname.split('/')
                    let newUrl = ''
                    if (marketCountry && isNewPasswordPath) {
                        if (/^\/feature/.test(window.location.pathname)) {
                            newUrl = `${window.location.protocol}//${window.location.host}/${pathnames[1]}/${pathnames[2]}/${pathnames[3]}/${marketCountry.country.toLowerCase()}/new-password/${pathnames[5]}/${window.location.search}`
                        } else if (/^\/epic/.test(window.location.pathname)) {
                            newUrl = `${window.location.protocol}//${window.location.host}/${pathnames[1]}/${pathnames[2]}/${pathnames[3]}/${pathnames[4]}/${pathnames[5]}/${marketCountry.country.toLowerCase()}/new-password/${pathnames[7]}/${window.location.search
                                }`
                        } else {
                            newUrl = `${window.location.protocol}//${window.location.host}/${marketCountry.country.toLowerCase()}/new-password/${pathnames[2]}/${window.location.search}`
                        }
                        diposer.dispose()
                        mixpanelCookieService.setIsInitialWithManyRedirects(true)
                        window.location.href = newUrl
                    }
                } else {
                    const isRedirect = checkFirstPathname()

                    if (isRedirect !== undefined) {
                        if (isRedirect) {
                            return
                        } else {
                            window.location.replace(`/select-country`)
                        }
                    }

                    diposer.dispose()
                }
            }
        })

        if (/\/select-country/.test(window.location.pathname)) {
            setGoSelectCountry(true)
        }
    }, [])

    useEffect(() => {
        const autoLoginToken = qs.searchParams.get('autoLogin')
        if (autoLoginToken) {
            localStorage.setItem('autoLogin', autoLoginToken)
            // Strip the token from the URL so re-renders / React Router can't re-seed it (prevents /login reload loop)
            qs.searchParams.delete('autoLogin')
            window.history.replaceState(null, '', qs.pathname + qs.search + qs.hash)
        }
    }, [qs])

    useEffect(() => {
        if (!storeAuth.IsAuthorized() && StoreCountry.Country3()) {
            checkGeoIPAccess()
            storeAuth.IsAllowAcceptLanguage()
        }
    }, [StoreCountry.Country3()])

    const checkGeoIPAccess = async () => {
        try {
            const { data } = await FeelGreatAPI.GetGeoIPAccessNoLogin({
                type: 'feelGreat',
                countryCode3Digit: StoreCountry.Country3()
            })

            if (!data.isPassAccess) {
                window.location.replace(`https://www.unicity.com`)
            }
        } catch (error) {
            console.error(error)
        }
    }

    useEffect(() => {
        const handlePageShow = event => {
            if (event.persisted && !localStorage.getItem('login-extends')) {
                window.location.reload()
            }
        }
        window.addEventListener('pageshow', handlePageShow)
        return () => window.removeEventListener('pageshow', handlePageShow)
    }, [])

    /*
        Initial referrer and initial referring domain
    */
    useEffect(() => {
        const isClient = typeof window !== 'undefined' && window !== null

        if (!isClient) {
            return
        }

        const initialReferrer = mixpanelCookieService.getInitialReferrer()

        if (!initialReferrer) {
            const isRefresh = window.performance?.getEntriesByType('navigation')[0]?.type === 'reload'

            if (isRefresh) {
                mixpanelCookieService.setInitialReferrer('$direct')
                mixpanelCookieService.setInitialReferringDomain('$direct')
                return
            }

            mixpanelCookieService.setInitialReferrer(document.referrer || '$direct')
            const referringDomain = document.referrer ? new URL(document.referrer).host : '$direct'

            mixpanelCookieService.setInitialReferringDomain(referringDomain)
        }
    }, [])

    return (
        <div className="App">
            <AccountConflictModal />
            {goSelectCountry ? (
                <AppRouter tiny={true} />
            ) : isMaintenanceMode() ? (
                <Maintenance />
            ) : (
                <>
                    <LoadingBar />
                    {StoreLoader.IsWebReady() && autoLoginLoader === false && <AppRouter />}
                    <CookieConsentModal />
                </>
            )}
        </div>
    )
})

const isBypass = () => {
    const bypassPath = BypassRedirectPath

    return bypassPath.some(path => window.location.pathname.includes(path))
}

const hasLanguage = (country, currentLanguageStorage = '') => {
    const countryLanguageList = get(country, 'config.languageSettings.items', [])
    return countryLanguageList.some(lang => (lang.code || '').toLowerCase() === currentLanguageStorage.toLowerCase())
}

export default App
